import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { map } from 'rxjs/operators';

import { ConfigService } from 'src/app/shared/config';

// Persistent settlement reconciliation (avstemming) per month. Stores the
// compact reconcile RESULT + metadata in Firestore (`avstemminger/{companyId}_{YYYY-MM}`)
// and archives the raw Vipps/Stripe report files in Cloud Storage
// (`avstemming/{companyId}/{YYYY-MM}/`). The raw settlement rows (16k+/month) are
// far too big for a 1 MB Firestore doc, so only the summarised result lives in
// Firestore; the source files live in Storage for audit + re-run.
@Injectable({ providedIn: 'root' })
export class AvstemmingService {

  constructor(
    private afs: AngularFirestore,
    private storage: AngularFireStorage,
    private config: ConfigService
  ) { }

  private docId(month: string) { return `${this.config.getCompanyId()}_${month}`; }

  // Upload one raw settlement file; returns its storage path + download URL.
  async uploadFile(month: string, kind: 'vipps' | 'stripe', file: File): Promise<{ path: string, url: string }> {
    const path = `avstemming/${this.config.getCompanyId()}/${month}/${kind}_${file.name}`;
    await this.storage.upload(path, file);
    const url = await this.storage.ref(path).getDownloadURL().toPromise();
    return { path, url };
  }

  // Fetch an archived settlement file back as a File, so a month can be re-run
  // without uploading the reports again. Needs CORS on the bucket for GET from
  // the admin origin.
  async downloadFile(path: string, fileName: string): Promise<File> {
    const url = await this.storage.ref(path).getDownloadURL().toPromise();
    const res = await fetch(url);
    if (!res.ok) { throw new Error(`HTTP ${res.status}`); }
    return new File([await res.blob()], fileName);
  }

  // Save (or overwrite the draft of) a month's avstemming.
  save(record: any): Promise<void> {
    const id = this.docId(record.period.month);
    return this.afs.collection('avstemminger').doc(id).set({
      ...record,
      companyId: this.config.getCompanyId(),
      updatedAt: Date.now()
    });
  }

  // Delete a month's saved avstemming.
  //
  // A saved avstemming is a SNAPSHOT of how a month was attributed when it ran,
  // not a live view: moving a group to another customer afterwards does not
  // rewrite it, and `save()` is hidden once you are viewing a saved one. So the
  // only way to redo a month after a structural change is to remove the old
  // snapshot and run it again. Settlement files in Storage are left alone —
  // they are the raw evidence and are what you would re-upload to re-run.
  async delete(month: string): Promise<void> {
    await this.deleteTransactions(month);
    return this.afs.collection('avstemminger').doc(this.docId(month)).delete();
  }

  // ---- per-transaction rows (the invoice specification) --------------------
  // One row per billed transaction, as returned by reconcileSettlements with
  // includeLines. 20 000+ rows a month is far over the 1 MB document limit, so
  // they are stored as JSON text in numbered chunk documents under the month's
  // avstemming: avstemminger/{id}/transactions/{000, 001, ...}.
  private transactionsCol(month: string) {
    return this.afs.firestore.collection('avstemminger').doc(this.docId(month)).collection('transactions');
  }

  async saveTransactions(month: string, columns: string[], rows: any[][]): Promise<{ rows: number, chunks: number }> {
    await this.deleteTransactions(month);
    // Chunk by UTF-8 size, not row count — product and buyer names vary, and
    // æ/ø/å take two bytes each against the 1 MiB document limit.
    const MAX_CHUNK_BYTES = 800000;
    const enc = new TextEncoder();
    const chunks: string[] = [];
    let cur: string[] = [];
    let size = 0;
    rows.forEach(r => {
      const s = JSON.stringify(r);
      const bytes = enc.encode(s).length + 1;
      if (cur.length && size + bytes > MAX_CHUNK_BYTES) { chunks.push(`[${cur.join(',')}]`); cur = []; size = 0; }
      cur.push(s); size += bytes;
    });
    if (cur.length) { chunks.push(`[${cur.join(',')}]`); }
    const col = this.transactionsCol(month);
    for (let i = 0; i < chunks.length; i++) {
      await col.doc(String(i).padStart(3, '0')).set({ index: i, columns, rows: chunks[i] });
    }
    return { rows: rows.length, chunks: chunks.length };
  }

  async loadTransactions(month: string): Promise<{ columns: string[], rows: any[][] }> {
    const snap = await this.transactionsCol(month).orderBy('index').get();
    let columns: string[] = [];
    const rows: any[][] = [];
    snap.forEach(d => {
      const data: any = d.data();
      columns = data.columns || columns;
      rows.push(...JSON.parse(data.rows || '[]'));
    });
    return { columns, rows };
  }

  private async deleteTransactions(month: string): Promise<void> {
    const snap = await this.transactionsCol(month).get();
    await Promise.all(snap.docs.map(d => d.ref.delete()));
  }

  // Every company's saved avstemminger, once — a kundekort's systems can sit in
  // more than one company.
  listAllOnce(): Promise<any[]> {
    return this.afs.collection('avstemminger').ref.get()
      .then(s => s.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
  }

  // All saved avstemminger for the company (sorted newest-first client-side to
  // avoid a composite index on companyId + month).
  list$() {
    return this.afs.collection('avstemminger', ref => ref
      .where('companyId', '==', this.config.getCompanyId()))
      .snapshotChanges()
      .pipe(map(ch => ch
        .map(c => ({ id: c.payload.doc.id, ...(c.payload.doc.data() as any) }))
        .sort((a, b) => String(b.period?.month || '').localeCompare(String(a.period?.month || '')))
      ));
  }
}
